import { computeLegacyFloor } from "@/lib/authz/capabilities";
import type { Capability } from "@/types/Capability";
import type { EventRoleDTO } from "@/types/EventRoleDTO";
import type { Filter } from "@/types/Filter";
import type { PreviewTarget } from "@/lib/preview/previewStore";

/**
 * The synthesized role envelope that drives the chrome during "preview as
 * role": the previewed role's capabilities and scope, spread over the admin's
 * own row so unrelated fields (ids, email, valid) stay intact.
 *
 * Extracted from `EventLayoutShell` so it can be tested — the shell needs a
 * router, two queries and a portal to mount, which is why the one field this
 * function exists to correct went unnoticed for as long as it did.
 *
 * @param eventRole  the ADMIN's real resolved envelope
 * @param target     the role being previewed
 * @param caps       live-edited caps (falls back to the target's own upstream)
 * @param fieldAllowlist live-edited allowlist — used for template targets only
 * @param rowFilters the filters the preview TOKEN was minted with — template
 *                   targets only; base roles keep their own (empty) scope
 */
export function buildPreviewRole(
  eventRole: EventRoleDTO,
  target: PreviewTarget,
  caps: Capability[],
  fieldAllowlist: string[] | null,
  rowFilters: Filter[],
): EventRoleDTO {
  const isBase = target.kind === "baseRole";
  return {
    ...eventRole,
    role: isBase ? (target.baseRole as string) : "CUSTOM",
    name: target.name,
    templateId: target.kind === "template" ? target.templateId : undefined,
    capabilities: caps,
    fieldAllowlist: isBase ? target.fieldAllowlist : fieldAllowlist,
    rowFilters: isBase ? target.rowFilters : rowFilters,
    // The previewed role's per-list defaults — NOT the admin's. Spreading
    // `eventRole` carried the admin's own starting view into the preview, so
    // the one thing this setting configures was the one thing a preview could
    // not show; an admin checking their work saw their own list and concluded
    // the default had not applied.
    surfaceDefaults: isBase ? [] : (target.surfaceDefaults ?? []),
    // Deliberately unstamped. `setAt` is the mark of "an admin wrote this for
    // YOU", and honouring it deletes the viewer's stored view/filter for that
    // list. Inside a preview that viewer is the admin themselves, so a stamped
    // envelope would let looking at a role destroy the admin's own setup.
    surfaceDefaultsSetAt: undefined,
    legacyRoleFloor: isBase ? undefined : computeLegacyFloor(new Set(caps)),
  };
}
